import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import MainLayout from '../../components/layout/MainLayout';
import cloudScanApi, {
    CloudProvider,
    ScanType,
    AWSCredentials,
    AzureCredentials,
    GCPCredentials,
    ScanResult,
    SecurityFinding,
    CostRecommendation,
    Severity,
    ScanHistoryItem,
} from '../../services/api/cloud-scan.api';
import billingApi from '../../services/api/billing.api';
import toast from 'react-hot-toast';
import { usePermissions } from '../../hooks/usePermissions';

// Provider icons
const ProviderIcon = ({ provider, className = 'w-8 h-8' }: { provider: CloudProvider; className?: string }) => {
    switch (provider) {
        case 'aws':
            return (
                <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M6.763 10.036c0 .296.032.535.088.71.064.176.144.368.256.576.04.063.056.127.056.183 0 .08-.048.16-.152.24l-.504.336a.38.38 0 0 1-.208.072c-.08 0-.16-.04-.239-.112a2.47 2.47 0 0 1-.287-.375 6.18 6.18 0 0 1-.248-.471c-.622.734-1.405 1.101-2.347 1.101-.67 0-1.205-.191-1.596-.574-.391-.384-.59-.894-.59-1.533 0-.678.239-1.23.726-1.644.487-.415 1.133-.623 1.955-.623.272 0 .551.024.846.064.296.04.6.104.918.176v-.583c0-.607-.127-1.03-.375-1.277-.255-.248-.686-.367-1.3-.367-.28 0-.568.031-.863.103-.296.072-.583.16-.862.272a2.287 2.287 0 0 1-.28.104.488.488 0 0 1-.127.023c-.112 0-.168-.08-.168-.247v-.391c0-.128.016-.224.056-.288a.596.596 0 0 1 .224-.167c.279-.144.614-.264 1.005-.36a4.84 4.84 0 0 1 1.246-.151c.95 0 1.644.216 2.091.647.439.432.662 1.085.662 1.963v2.586zm-3.24 1.214c.263 0 .534-.048.822-.144.287-.096.543-.271.758-.51.128-.152.224-.32.272-.512.047-.191.08-.423.08-.694v-.335a6.66 6.66 0 0 0-.735-.136 6.02 6.02 0 0 0-.75-.048c-.535 0-.926.104-1.19.32-.263.215-.39.518-.39.917 0 .375.095.655.295.846.191.2.47.296.838.296zm6.41.862c-.144 0-.24-.024-.304-.08-.063-.048-.12-.16-.168-.311L7.586 5.55a1.398 1.398 0 0 1-.072-.32c0-.128.064-.2.191-.2h.783c.151 0 .255.025.31.08.065.048.113.16.16.312l1.342 5.284 1.245-5.284c.04-.16.088-.264.151-.312a.549.549 0 0 1 .32-.08h.638c.152 0 .256.025.32.08.063.048.12.16.151.312l1.261 5.348 1.381-5.348c.048-.16.104-.264.16-.312a.52.52 0 0 1 .311-.08h.743c.127 0 .2.065.2.2 0 .04-.009.08-.017.128a1.137 1.137 0 0 1-.056.2l-1.923 6.17c-.048.16-.104.263-.168.311a.51.51 0 0 1-.303.08h-.687c-.151 0-.255-.024-.32-.08-.063-.056-.119-.16-.15-.32l-1.238-5.148-1.23 5.14c-.04.16-.087.264-.15.32-.065.056-.177.08-.32.08zm10.256.215c-.415 0-.83-.048-1.229-.143-.399-.096-.71-.2-.918-.32-.128-.071-.215-.151-.247-.223a.563.563 0 0 1-.048-.224v-.407c0-.167.064-.247.183-.247.048 0 .096.008.144.024.048.016.12.048.2.08.271.12.566.215.878.279.319.064.63.096.95.096.502 0 .894-.088 1.165-.264a.86.86 0 0 0 .415-.758.777.777 0 0 0-.215-.559c-.144-.151-.415-.287-.806-.415l-1.157-.36c-.583-.183-1.013-.454-1.277-.814a1.993 1.993 0 0 1-.4-1.197c0-.343.072-.647.215-.91.144-.264.335-.488.574-.672.24-.184.51-.32.83-.415a3.81 3.81 0 0 1 1.022-.136c.18 0 .367.008.55.032.192.024.367.056.535.088.16.04.312.08.455.127.144.048.256.096.336.144a.69.69 0 0 1 .24.2.43.43 0 0 1 .071.263v.375c0 .168-.064.256-.184.256a.83.83 0 0 1-.303-.096 3.652 3.652 0 0 0-1.532-.311c-.455 0-.815.071-1.062.223-.248.152-.375.383-.375.694 0 .224.08.416.24.567.159.152.454.304.877.44l1.134.358c.574.184.99.44 1.237.767.247.327.367.702.367 1.117 0 .343-.072.655-.207.926-.144.272-.336.511-.583.703-.248.2-.543.343-.886.447-.36.111-.734.167-1.142.167z" />
                </svg>
            );
        case 'azure':
            return (
                <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M5.483 21.3H24L14.025 4.013l-3.038 8.347 5.836 6.938L5.483 21.3zM13.23 2.7L6.105 8.677 0 19.253h5.505v.014L13.23 2.7z" />
                </svg>
            );
        case 'gcp':
            return (
                <svg className={className} viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12.19 2.38a9.344 9.344 0 0 0-9.234 6.893c.053-.02-.055.013 0 0-3.875 2.551-3.922 8.11-.247 10.941l.006-.007-.007.03a6.717 6.717 0 0 0 4.077 1.356h5.173l.03.03h5.192c6.687.053 9.376-8.605 3.835-12.35a9.365 9.365 0 0 0-8.825-6.893zM8.073 19.39a4.292 4.292 0 0 1-2.49-.756l.03-.036.007.013a4.322 4.322 0 0 1-1.59-3.341c0-2.38 1.927-4.307 4.307-4.307.617 0 1.206.13 1.738.366l.03-.03 2.403-2.403a7.227 7.227 0 0 0-4.17-1.326 7.203 7.203 0 0 0-7.203 7.2c0 1.987.805 3.787 2.107 5.09a7.175 7.175 0 0 0 5.095 2.11h5.173v-2.58H8.073zm8.095-.036h5.222a6.853 6.853 0 0 0 2.053-4.887 6.854 6.854 0 0 0-6.86-6.86 6.827 6.827 0 0 0-4.183 1.423l2.403 2.403a4.273 4.273 0 0 1 1.78-.39 4.292 4.292 0 0 1 4.293 4.28 4.268 4.268 0 0 1-1.266 3.054 4.254 4.254 0 0 1-3.027 1.254h-5.216l-2.574 2.574 2.574-.001h4.8z" />
                </svg>
            );
    }
};

// Severity badge colors
const getSeverityColor = (severity: Severity) => {
    switch (severity) {
        case 'critical':
            return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200 dark:border-red-800';
        case 'high':
            return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200 dark:border-orange-800';
        case 'medium':
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400 border-yellow-200 dark:border-yellow-800';
        case 'low':
            return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400 border-blue-200 dark:border-blue-800';
        case 'info':
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400 border-gray-200 dark:border-gray-800';
    }
};

// Provider colors
const getProviderColor = (provider: CloudProvider) => {
    switch (provider) {
        case 'aws':
            return 'from-orange-500 to-yellow-500';
        case 'azure':
            return 'from-blue-500 to-cyan-500';
        case 'gcp':
            return 'from-green-500 to-blue-500';
    }
};

export default function CloudScanPage() {
    const { t, i18n } = useTranslation(['cloudScan', 'common']);
    const { canUseCloudScan } = usePermissions();
    const user = useSelector((state: RootState) => state.auth.user);

    // State
    const [selectedProvider, setSelectedProvider] = useState<CloudProvider | null>(null);
    const [scanType, setScanType] = useState<ScanType>('full');
    const [isValidating, setIsValidating] = useState(false);
    const [isScanning, setIsScanning] = useState(false);
    const [validationMessage, setValidationMessage] = useState<{ type: 'success' | 'error'; message: string } | null>(null);
    const [scanResult, setScanResult] = useState<ScanResult | null>(null);
    const [activeTab, setActiveTab] = useState<'security' | 'cost'>('security');
    const [expandedFinding, setExpandedFinding] = useState<string | null>(null);
    const [showPermissions, setShowPermissions] = useState(false);

    // Scan history state
    const [scanHistory, setScanHistory] = useState<ScanHistoryItem[]>([]);
    const [historyLoading, setHistoryLoading] = useState(true);
    const [showHistory, setShowHistory] = useState(true);

    // Subscription state
    const [subscription, setSubscription] = useState<any>(null);
    const [subscriptionLoading, setSubscriptionLoading] = useState(false);
    const [hasPremiumAccess, setHasPremiumAccess] = useState(false);

    // Usage info state (monthly scan limits)
    const [usageInfo, setUsageInfo] = useState<{
        tier: string;
        monthlyLimit: number;
        monthlyUsed: number;
        monthlyRemaining: number;
        canMakeFreeScan: boolean;
    } | null>(null);

    // AWS Credentials
    const [awsCredentials, setAwsCredentials] = useState<AWSCredentials>({
        accessKeyId: '',
        secretAccessKey: '',
        region: 'us-east-1',
    });

    // Azure Credentials
    const [azureCredentials, setAzureCredentials] = useState<AzureCredentials>({
        tenantId: '',
        clientId: '',
        clientSecret: '',
        subscriptionId: '',
    });

    // GCP Credentials
    const [gcpCredentials, setGcpCredentials] = useState<GCPCredentials>({
        projectId: '',
        clientEmail: '',
        privateKey: '',
    });

    const regions = [
        { value: 'us-east-1', label: 'US East (N. Virginia)' },
        { value: 'us-east-2', label: 'US East (Ohio)' },
        { value: 'us-west-1', label: 'US West (N. California)' },
        { value: 'us-west-2', label: 'US West (Oregon)' },
        { value: 'eu-west-1', label: 'EU (Ireland)' },
        { value: 'eu-west-2', label: 'EU (London)' },
        { value: 'eu-central-1', label: 'EU (Frankfurt)' },
        { value: 'ap-southeast-1', label: 'Asia Pacific (Singapore)' },
        { value: 'ap-southeast-2', label: 'Asia Pacific (Sydney)' },
        { value: 'ap-northeast-1', label: 'Asia Pacific (Tokyo)' },
        { value: 'sa-east-1', label: 'South America (São Paulo)' },
    ];

    const getCredentials = () => {
        switch (selectedProvider) {
            case 'aws':
                return awsCredentials;
            case 'azure':
                return azureCredentials;
            case 'gcp':
                return gcpCredentials;
            default:
                return null;
        }
    };

    // Load scan history on component mount
    const loadScanHistory = async () => {
        try {
            setHistoryLoading(true);
            const response = await cloudScanApi.getScanHistory({ limit: 10 });
            if (response.success) {
                setScanHistory(response.data.scans);
            }
        } catch (error) {
            console.error('Failed to load scan history:', error);
        } finally {
            setHistoryLoading(false);
        }
    };

    useEffect(() => {
        loadScanHistory();
        loadSubscription();
        loadUsageInfo();
    }, []);

    // Reload history after a scan completes
    useEffect(() => {
        if (scanResult) {
            loadScanHistory();
        }
    }, [scanResult]);

    const loadSubscription = async () => {
        try {
            setSubscriptionLoading(true);
            const response = await billingApi.getCurrentSubscription();
            if (response.success && response.data) {
                setSubscription(response.data);
                const tier = response.data.plan?.tier || 'free';
                setHasPremiumAccess(tier === 'professional' || tier === 'enterprise');
            }
        } catch (error) {
            console.error('Failed to load subscription:', error);
        } finally {
            setSubscriptionLoading(false);
        }
    };

    const loadUsageInfo = async () => {
        try {
            const response = await cloudScanApi.getScanUsageInfo();
            if (response.success) {
                setUsageInfo(response.data);
            }
        } catch (error) {
            console.error('Failed to load usage info:', error);
        }
    };

    const handleDeleteScan = async (scanId: string, e: React.MouseEvent) => {
        e.stopPropagation();
        if (!confirm(t('cloudScan:confirmDelete', 'Are you sure you want to delete this scan?'))) {
            return;
        }
        try {
            const response = await cloudScanApi.deleteScan(scanId);
            if (response.success) {
                toast.success(t('cloudScan:deleteSuccess', 'Scan deleted successfully'), {
                    duration: 3000,
                    icon: '🗑️',
                });
                // Reload history
                await loadScanHistory();
            }
        } catch (error: any) {
            console.error('Failed to delete scan:', error);
            toast.error(error.response?.data?.error || t('cloudScan:deleteError', 'Failed to delete scan'), {
                duration: 5000,
                icon: '❌',
            });
        }
    };

    const handlePurchaseScan = async (scanId: string) => {
        try {
            const response = await cloudScanApi.purchaseScan(scanId);
            if (response.success) {
                // Reload scan data
                if (scanResult && scanResult.scanId === scanId) {
                    const updatedResponse = await cloudScanApi.getScan(scanId);
                    if (updatedResponse.success) {
                        setScanResult({
                            ...updatedResponse.data,
                            scanId: updatedResponse.scanId,
                            hasPremiumAccess: updatedResponse.hasPremiumAccess || hasPremiumAccess,
                            effectiveIsPaid: updatedResponse.effectiveIsPaid || updatedResponse.isPaid || true,
                        });
                    }
                }
                // Reload history and usage info
                await loadScanHistory();
                await loadUsageInfo();
                toast.success(t('common:purchaseSuccess', 'Scan report purchased successfully!'), {
                    duration: 3000,
                    icon: '✅',
                });
            }
        } catch (error: any) {
            console.error('Failed to purchase scan:', error);
            toast.error(error.response?.data?.error || t('common:purchaseError', 'Failed to purchase scan report'), {
                duration: 5000,
                icon: '❌',
            });
        }
    };

    const handleValidateCredentials = async () => {
        if (!selectedProvider) return;

        setIsValidating(true);
        setValidationMessage(null);

        try {
            const response = await cloudScanApi.validateCredentials({
                provider: selectedProvider,
                credentials: getCredentials()!,
            });

            if (response.data.valid) {
                setValidationMessage({ type: 'success', message: response.data.message });
            } else {
                setValidationMessage({ type: 'error', message: response.data.message });
            }
        } catch (error: any) {
            setValidationMessage({ type: 'error', message: error.response?.data?.message || t('errors.validateFailed') });
        } finally {
            setIsValidating(false);
        }
    };

    const handleStartScan = async () => {
        // Validation
        if (!selectedProvider) {
            setValidationMessage({ type: 'error', message: t('cloudScan:errors.selectProvider') });
            return;
        }

        // Validate credentials are filled
        const credentials = getCredentials();
        if (!credentials) {
            setValidationMessage({ type: 'error', message: t('cloudScan:errors.missingCredentials') });
            return;
        }

        // Validate provider-specific required fields
        if (selectedProvider === 'aws') {
            const aws = credentials as AWSCredentials;
            if (!aws.accessKeyId || !aws.secretAccessKey) {
                setValidationMessage({ type: 'error', message: t('cloudScan:errors.missingCredentials') });
                return;
            }
        } else if (selectedProvider === 'azure') {
            const azure = credentials as AzureCredentials;
            if (!azure.tenantId || !azure.clientId || !azure.clientSecret || !azure.subscriptionId) {
                setValidationMessage({ type: 'error', message: t('cloudScan:errors.missingCredentials') });
                return;
            }
        } else if (selectedProvider === 'gcp') {
            const gcp = credentials as GCPCredentials;
            if (!gcp.projectId || !gcp.clientEmail || !gcp.privateKey) {
                setValidationMessage({ type: 'error', message: t('cloudScan:errors.missingCredentials') });
                return;
            }
        }

        setIsScanning(true);
        setScanResult(null);
        setValidationMessage(null);

        try {
            const response = await cloudScanApi.startScan({
                provider: selectedProvider,
                credentials: credentials,
                scanType,
            });

            if (response.success && response.scanId) {
                // Redirect to details page
                window.location.href = `/cloud-scan/${response.scanId}`;
            }
        } catch (error: any) {
            console.error('Scan error:', error);

            // Parse and display specific error messages
            let errorMessage = t('cloudScan:errors.scanFailed');

            if (error.response) {
                const status = error.response.status;
                const data = error.response.data;

                // Handle different error types
                if (status === 400) {
                    // Validation error
                    if (data.error === 'Validation error' && data.details) {
                        const firstError = data.details[0];
                        errorMessage = firstError.message || t('cloudScan:errors.validationError');
                    } else {
                        errorMessage = data.message || data.error || t('cloudScan:errors.validationError');
                    }
                } else if (status === 401) {
                    // Authentication error
                    errorMessage = t('cloudScan:errors.authenticationFailed');
                } else if (status === 403) {
                    // Permission denied or quota exceeded
                    if (data.error && data.error.includes('quota')) {
                        errorMessage = t('cloudScan:errors.quotaExceeded');
                    } else {
                        errorMessage = data.error || data.message || t('cloudScan:errors.permissionDenied');
                    }
                } else if (status === 404) {
                    errorMessage = t('cloudScan:errors.notFound');
                } else if (status >= 500) {
                    // Server error
                    errorMessage = data.message || t('cloudScan:errors.serverError');
                } else {
                    // Other errors - try to use the message from the server
                    errorMessage = data.message || data.error || t('cloudScan:errors.scanFailed');
                }
            } else if (error.request) {
                // Network error
                errorMessage = t('cloudScan:errors.networkError');
            } else {
                // Other errors
                errorMessage = error.message || t('cloudScan:errors.scanFailed');
            }

            setValidationMessage({ type: 'error', message: errorMessage });
        } finally {
            setIsScanning(false);
        }
    };

    const formatCurrency = (value: number) => {
        const currency = (user?.tenant as any)?.currency || 'USD';
        const locale = currency === 'BRL' ? 'pt-BR' : 'en-US';
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
        }).format(value);
    };

    const formatDuration = (ms: number | undefined) => {
        if (!ms || isNaN(ms)) return '0s';
        if (ms < 1000) return `${ms}ms`;
        if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
        return `${(ms / 60000).toFixed(1)}m`;
    };

    const formatTimestamp = (timestamp: string | number | undefined) => {
        if (!timestamp) return t('common:notAvailable', 'N/A');
        try {
            const date = new Date(timestamp);
            if (isNaN(date.getTime())) return t('common:notAvailable', 'N/A');
            return date.toLocaleString(i18n.language);
        } catch (error) {
            return t('common:notAvailable', 'N/A');
        }
    };

    return (
        <MainLayout>
            <div className="mb-6 sm:mb-8">
                <div className="flex items-start sm:items-center gap-3 mb-2">
                    <div className="flex h-10 w-10 sm:h-12 sm:w-12 items-center justify-center rounded-xl bg-gradient-to-br from-indigo-500 to-purple-600 shadow-lg shadow-indigo-500/30 flex-shrink-0">
                        <svg className="w-5 h-5 sm:w-6 sm:h-6 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 15a4 4 0 004 4h9a5 5 0 10-.1-9.999 5.002 5.002 0 10-9.78 2.096A4.001 4.001 0 003 15z" />
                        </svg>
                    </div>
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">{t('title')}</h1>
                        <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">{t('subtitle')}</p>
                    </div>
                </div>
            </div>

            {/* Value Proposition Section */}
            {!scanResult && (
                <div className="mb-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Benefit 1 - Security */}
                    <div className="rounded-xl border border-red-200 dark:border-red-900/30 bg-gradient-to-br from-red-50 to-orange-50 dark:from-red-950/20 dark:to-orange-950/20 p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="p-2.5 rounded-lg bg-red-100 dark:bg-red-900/50 text-red-600 dark:text-red-400">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                            </div>
                            <h3 className="font-bold text-gray-900 dark:text-white">{t('cloudScan:benefits.security.title', 'Security Analysis')}</h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                            {t('cloudScan:benefits.security.description', 'Detect vulnerabilities, misconfigurations, and security risks across your cloud infrastructure. IAM policies, network security, encryption, and more.')}
                        </p>
                    </div>

                    {/* Benefit 2 - Cost Optimization */}
                    <div className="rounded-xl border border-green-200 dark:border-green-900/30 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950/20 dark:to-emerald-950/20 p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="p-2.5 rounded-lg bg-green-100 dark:bg-green-900/50 text-green-600 dark:text-green-400">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                            </div>
                            <h3 className="font-bold text-gray-900 dark:text-white">{t('cloudScan:benefits.cost.title', 'Cost Optimization')}</h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                            {t('cloudScan:benefits.cost.description', 'Identify idle resources, underutilized instances, and opportunities to reduce cloud spending. Actionable recommendations to optimize costs.')}
                        </p>
                    </div>

                    {/* Benefit 3 - AI Insights */}
                    <div className="rounded-xl border border-purple-200 dark:border-purple-900/30 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950/20 dark:to-pink-950/20 p-6">
                        <div className="flex items-center gap-3 mb-3">
                            <div className="p-2.5 rounded-lg bg-purple-100 dark:bg-purple-900/50 text-purple-600 dark:text-purple-400">
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                            </div>
                            <h3 className="font-bold text-gray-900 dark:text-white">{t('cloudScan:benefits.ai.title', 'AI-Powered Insights')}</h3>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 leading-relaxed">
                            {t('cloudScan:benefits.ai.description', 'Automatic incident creation, anomaly detection, cost forecasting, and intelligent recommendations powered by machine learning.')}
                        </p>
                    </div>
                </div>
            )}

            {/* Usage Info Banner */}
            {usageInfo && !scanResult && (
                <div className="mb-6 sm:mb-8 rounded-xl border border-indigo-200 bg-gradient-to-r from-indigo-50 to-purple-50 p-4 sm:p-6 dark:border-indigo-800 dark:from-indigo-900/20 dark:to-purple-900/20">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                        <div>
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white mb-1 sm:mb-2">
                                {t('cloudScan:usageInfo.title', 'Your Cloud Scan Plan')}
                            </h3>
                            <p className="text-xs sm:text-sm text-gray-600 dark:text-gray-400">
                                {usageInfo.tier === 'professional' && t('cloudScan:usageInfo.professional', 'Professional Plan: 1 scan per month included')}
                                {usageInfo.tier === 'enterprise' && t('cloudScan:usageInfo.enterprise', 'Enterprise Plan: 5 scans per month included')}
                                {(usageInfo.tier === 'free' || usageInfo.tier === 'starter') && t('cloudScan:usageInfo.freeStarter', 'No scans included - Purchase scans individually at $9.99 each')}
                            </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 sm:gap-6">
                            <div className="text-center">
                                <div className="text-2xl sm:text-3xl font-bold text-indigo-600 dark:text-indigo-400">
                                    {usageInfo.monthlyUsed}/{usageInfo.monthlyLimit < 0 ? '∞' : usageInfo.monthlyLimit}
                                </div>
                                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                    {t('cloudScan:usageInfo.used', 'Scans Used This Month')}
                                </div>
                            </div>
                            {usageInfo.monthlyLimit < 0 && (
                                <div className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-800 dark:text-purple-400 text-xs sm:text-sm font-semibold">
                                    ∞ {t('cloudScan:usageInfo.unlimited', 'Unlimited scans')}
                                </div>
                            )}
                            {usageInfo.monthlyRemaining > 0 && usageInfo.monthlyLimit >= 0 && (
                                <div className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400 text-xs sm:text-sm font-semibold">
                                    ✓ {usageInfo.monthlyRemaining} {t('cloudScan:usageInfo.remaining', 'scans remaining')}
                                </div>
                            )}
                            {usageInfo.monthlyLimit === 0 && (
                                <Link
                                    to="/settings/billing"
                                    className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-lg bg-indigo-600 text-white text-xs sm:text-sm font-semibold hover:bg-indigo-700 transition-colors"
                                >
                                    {t('cloudScan:usageInfo.upgrade', 'Upgrade Plan')}
                                </Link>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {/* My Scans History Section */}
            {!scanResult && (
                <div className="mb-8">
                    <button
                        onClick={() => setShowHistory(!showHistory)}
                        className="flex items-center gap-2 text-lg font-semibold text-gray-900 dark:text-white mb-4 hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors"
                    >
                        <svg
                            className={`w-5 h-5 transition-transform ${showHistory ? 'rotate-90' : ''}`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                        >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                        </svg>
                        📋 {t('history.title', 'Meus Scans')} ({scanHistory.length})
                    </button>

                    {showHistory && (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
                            {historyLoading ? (
                                <div className="p-8 text-center">
                                    <svg className="animate-spin w-8 h-8 mx-auto text-indigo-500" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    <p className="mt-2 text-gray-500 dark:text-gray-400">{t('history.loading', 'Carregando histórico...')}</p>
                                </div>
                            ) : scanHistory.length === 0 ? (
                                <div className="p-8 text-center">
                                    <div className="inline-flex p-4 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-400 mb-4">
                                        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                        </svg>
                                    </div>
                                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                                        {t('history.empty.title', 'Nenhum scan realizado')}
                                    </h3>
                                    <p className="text-gray-500 dark:text-gray-400">
                                        {t('history.empty.description', 'Execute seu primeiro scan selecionando um provider abaixo.')}
                                    </p>
                                </div>
                            ) : (
                                <div className="divide-y divide-gray-200 dark:divide-gray-800">
                                    {scanHistory.map((scan) => (
                                        <div
                                            key={scan.id}
                                            className="p-3 sm:p-4 hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
                                        >
                                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
                                                {/* Provider info */}
                                                <div className="flex items-center gap-3 min-w-0">
                                                    <div className={`p-2 rounded-lg bg-gradient-to-br ${getProviderColor(scan.provider)} text-white flex-shrink-0`}>
                                                        <ProviderIcon provider={scan.provider} className="w-4 h-4 sm:w-5 sm:h-5" />
                                                    </div>
                                                    <div className="min-w-0 flex-1">
                                                        <div className="flex flex-wrap items-center gap-1 sm:gap-2">
                                                            <span className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">
                                                                {scan.provider.toUpperCase()}
                                                            </span>
                                                            <span className={`px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium ${scan.status === 'completed'
                                                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                                                    : scan.status === 'failed'
                                                                        ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                                                                        : scan.status === 'running'
                                                                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                                                            : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400'
                                                                }`}>
                                                                {scan.status}
                                                            </span>
                                                            {scan.isPaid && (
                                                                <span className="px-1.5 sm:px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400">
                                                                    💎 Premium
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5 truncate">
                                                            {formatTimestamp(scan.createdAt)} • {scan.scanType}
                                                        </div>
                                                    </div>
                                                </div>
                                                {/* Stats and action */}
                                                <div className="flex items-center justify-between sm:justify-end gap-3 sm:gap-6 pl-9 sm:pl-0">
                                                    <div className="text-left sm:text-right">
                                                        <div className="text-xs sm:text-sm">
                                                            <span className="text-red-600 dark:text-red-400 font-medium">{scan.totalSecurityFindings}</span>
                                                            <span className="text-gray-500 dark:text-gray-400"> issues</span>
                                                            {scan.criticalIssues > 0 && (
                                                                <span className="text-red-600 dark:text-red-400 ml-1">
                                                                    ({scan.criticalIssues} crit)
                                                                </span>
                                                            )}
                                                        </div>
                                                        <div className="text-xs sm:text-sm text-green-600 dark:text-green-400">
                                                            ${scan.estimatedMonthlySavings?.toFixed(2) || '0.00'}/mo
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => window.location.href = `/cloud-scan/${scan.id}`}
                                                            className="px-2 sm:px-3 py-1.5 rounded-lg text-xs sm:text-sm font-medium bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors whitespace-nowrap"
                                                        >
                                                            <span className="hidden sm:inline">{t('history.viewDetails', 'Ver Detalhes')} →</span>
                                                            <span className="sm:hidden">Ver →</span>
                                                        </button>
                                                        <button
                                                            onClick={(e) => handleDeleteScan(scan.id, e)}
                                                            className="p-1.5 rounded-lg text-xs sm:text-sm font-medium bg-red-50 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/50 transition-colors"
                                                            title={t('common:delete', 'Delete')}
                                                        >
                                                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                                            </svg>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* Permissions Warning */}
            {!canUseCloudScan && !scanResult && (
                <div className="mb-8 rounded-xl border border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50 p-6 dark:border-amber-800 dark:from-amber-900/20 dark:to-orange-900/20">
                    <div className="flex items-start gap-3">
                        <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/50 text-amber-600 dark:text-amber-400">
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                                {t('permissions.noAccess.title', 'View Only Access')}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400">
                                {t('permissions.noAccess.description', 'You have view-only access and cannot perform cloud scans. Contact your administrator to upgrade your permissions.')}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Provider Selection */}
            {!scanResult && (
                <div className="mb-8">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('selectProvider')}</h2>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {(['aws', 'azure', 'gcp'] as CloudProvider[]).map((provider) => (
                            <button
                                key={provider}
                                onClick={() => {
                                    if (!canUseCloudScan) return;
                                    setSelectedProvider(provider);
                                    setValidationMessage(null);
                                    setScanResult(null);
                                }}
                                disabled={!canUseCloudScan}
                                className={`relative overflow-hidden rounded-2xl border-2 p-6 transition-all duration-300 ${
                                    !canUseCloudScan
                                        ? 'opacity-50 cursor-not-allowed border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
                                        : selectedProvider === provider
                                            ? 'border-indigo-500 bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-950/50 dark:to-purple-950/50 shadow-lg shadow-indigo-500/20'
                                            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-md'
                                }`}
                            >
                                <div className={`absolute top-0 right-0 w-32 h-32 rounded-full blur-3xl opacity-20 bg-gradient-to-br ${getProviderColor(provider)}`} />
                                <div className="relative">
                                    <div className={`inline-flex p-3 rounded-xl mb-4 bg-gradient-to-br ${getProviderColor(provider)} text-white shadow-lg`}>
                                        <ProviderIcon provider={provider} className="w-8 h-8" />
                                    </div>
                                    <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-1">
                                        {t(`providers.${provider}.name`)}
                                    </h3>
                                    <p className="text-sm text-gray-600 dark:text-gray-400">
                                        {t(`providers.${provider}.description`)}
                                    </p>
                                    {selectedProvider === provider && (
                                        <div className="absolute top-2 right-2">
                                            <svg className="w-6 h-6 text-indigo-500" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                    )}
                                </div>
                            </button>
                        ))}
                    </div>
                </div>
            )}

            {/* Credentials Form */}
            {selectedProvider && !scanResult && (
                <div className="mb-8 rounded-2xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                        <div className={`inline-flex p-2 rounded-lg bg-gradient-to-br ${getProviderColor(selectedProvider)} text-white`}>
                            <ProviderIcon provider={selectedProvider} className="w-5 h-5" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                            {selectedProvider.toUpperCase()} {t('credentials.title')}
                        </h3>
                    </div>

                    {/* AWS Credentials Form */}
                    {selectedProvider === 'aws' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('credentials.aws.accessKeyId')} *
                                </label>
                                <input
                                    type="text"
                                    value={awsCredentials.accessKeyId}
                                    onChange={(e) => setAwsCredentials({ ...awsCredentials, accessKeyId: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                                    placeholder="AKIAIOSFODNN7EXAMPLE"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('credentials.aws.secretAccessKey')} *
                                </label>
                                <input
                                    type="password"
                                    value={awsCredentials.secretAccessKey}
                                    onChange={(e) => setAwsCredentials({ ...awsCredentials, secretAccessKey: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                                    placeholder="••••••••••••••••••••••••"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('credentials.aws.region')} *
                                </label>
                                <select
                                    value={awsCredentials.region}
                                    onChange={(e) => setAwsCredentials({ ...awsCredentials, region: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                                >
                                    {regions.map((region) => (
                                        <option key={region.value} value={region.value}>
                                            {region.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('credentials.aws.sessionToken')}
                                </label>
                                <input
                                    type="password"
                                    value={awsCredentials.sessionToken || ''}
                                    onChange={(e) => setAwsCredentials({ ...awsCredentials, sessionToken: e.target.value || undefined })}
                                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                                    placeholder={t('credentials.aws.sessionTokenPlaceholder')}
                                />
                            </div>
                        </div>
                    )}

                    {/* Azure Credentials Form */}
                    {selectedProvider === 'azure' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('credentials.azure.tenantId')} *
                                </label>
                                <input
                                    type="text"
                                    value={azureCredentials.tenantId}
                                    onChange={(e) => setAzureCredentials({ ...azureCredentials, tenantId: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('credentials.azure.clientId')} *
                                </label>
                                <input
                                    type="text"
                                    value={azureCredentials.clientId}
                                    onChange={(e) => setAzureCredentials({ ...azureCredentials, clientId: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('credentials.azure.clientSecret')} *
                                </label>
                                <input
                                    type="password"
                                    value={azureCredentials.clientSecret}
                                    onChange={(e) => setAzureCredentials({ ...azureCredentials, clientSecret: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                                    placeholder="••••••••••••••••••••••••"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('credentials.azure.subscriptionId')} *
                                </label>
                                <input
                                    type="text"
                                    value={azureCredentials.subscriptionId}
                                    onChange={(e) => setAzureCredentials({ ...azureCredentials, subscriptionId: e.target.value })}
                                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                                    placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                                />
                            </div>
                        </div>
                    )}

                    {/* GCP Credentials Form */}
                    {selectedProvider === 'gcp' && (
                        <div className="grid grid-cols-1 gap-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {t('credentials.gcp.projectId')} *
                                    </label>
                                    <input
                                        type="text"
                                        value={gcpCredentials.projectId}
                                        onChange={(e) => setGcpCredentials({ ...gcpCredentials, projectId: e.target.value })}
                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                                        placeholder="my-project-123456"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                        {t('credentials.gcp.clientEmail')} *
                                    </label>
                                    <input
                                        type="email"
                                        value={gcpCredentials.clientEmail}
                                        onChange={(e) => setGcpCredentials({ ...gcpCredentials, clientEmail: e.target.value })}
                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors"
                                        placeholder="service-account@project.iam.gserviceaccount.com"
                                    />
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    {t('credentials.gcp.privateKey')} *
                                </label>
                                <textarea
                                    value={gcpCredentials.privateKey}
                                    onChange={(e) => setGcpCredentials({ ...gcpCredentials, privateKey: e.target.value })}
                                    rows={4}
                                    className="w-full rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 px-4 py-2.5 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/20 transition-colors font-mono text-sm"
                                    placeholder="-----BEGIN PRIVATE KEY-----&#10;...&#10;-----END PRIVATE KEY-----"
                                />
                            </div>
                        </div>
                    )}

                    {/* Required Permissions Section */}
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-800">
                        <button
                            onClick={() => setShowPermissions(!showPermissions)}
                            className="flex items-center gap-2 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 transition-colors"
                        >
                            <svg
                                className={`w-4 h-4 transition-transform ${showPermissions ? 'rotate-180' : ''}`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                            </svg>
                            {showPermissions ? t('actions.hidePermissions') : t('actions.showPermissions')}
                        </button>

                        {showPermissions && (
                            <div className="mt-4 rounded-xl border border-indigo-100 dark:border-indigo-900/50 bg-indigo-50/50 dark:bg-indigo-950/30 p-5">
                                <div className="flex items-start gap-3 mb-4">
                                    <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/50 text-indigo-600 dark:text-indigo-400">
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h4 className="font-semibold text-gray-900 dark:text-white">
                                            {t(`permissions.${selectedProvider}.title`)}
                                        </h4>
                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                            {t(`permissions.${selectedProvider}.description`)}
                                        </p>
                                    </div>
                                </div>

                                {/* Recommended Policies/Roles */}
                                <div className="mb-4">
                                    <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                        📋 {selectedProvider === 'aws' ? t('permissions.aws.policiesTitle', 'Recommended Managed Policies:') : t(`permissions.${selectedProvider}.rolesTitle`, 'Recommended Roles:')}
                                    </h5>
                                    <ul className="space-y-1">
                                        {(t(`permissions.${selectedProvider}.${selectedProvider === 'aws' ? 'policies' : 'roles'}`, { returnObjects: true }) as string[]).map((item, index) => (
                                            <li key={index} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                                                <svg className="w-4 h-4 mt-0.5 text-green-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                                                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                </svg>
                                                <code className="bg-gray-100 dark:bg-gray-800 px-1.5 py-0.5 rounded text-xs font-mono">{item}</code>
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Specific Permissions */}
                                <div className="mb-4">
                                    <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                                        {t(`permissions.${selectedProvider}.services.title`)}
                                    </h5>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                        {(t(`permissions.${selectedProvider}.services.list`, { returnObjects: true }) as string[]).map((permission, index) => (
                                            <div key={index} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-400">
                                                <span className="text-indigo-400 mt-1">•</span>
                                                <code className="font-mono break-all">{permission}</code>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                {/* Tip */}
                                <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/50">
                                    <span className="text-amber-500 text-lg">💡</span>
                                    <p className="text-sm text-amber-800 dark:text-amber-400">
                                        {t(`permissions.${selectedProvider}.note`)}
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Scan Type Selection */}
                    <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-800">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-4">
                            {t('scanType.label')} - Choose Your Analysis
                        </label>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {/* Full Scan */}
                            <button
                                onClick={() => setScanType('full')}
                                className={`relative p-5 rounded-xl border-2 transition-all text-left ${scanType === 'full'
                                    ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30 shadow-lg shadow-indigo-500/20'
                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-indigo-300 dark:hover:border-indigo-700'
                                    }`}
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <span className="text-2xl">🔍</span>
                                    {scanType === 'full' && (
                                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-indigo-500 text-white">
                                            Selected
                                        </span>
                                    )}
                                </div>
                                <h4 className="font-bold text-gray-900 dark:text-white mb-1">
                                    {t('scanType.full')}
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                    {t('cloudScan:benefits.security.title', 'Security Analysis')} + {t('cloudScan:benefits.cost.title', 'Cost Optimization')}
                                </p>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-2xl font-bold text-gray-900 dark:text-white">$9.99</span>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">/scan</span>
                                </div>
                                {usageInfo && usageInfo.canMakeFreeScan && (
                                    <div className="mt-2 px-2 py-1 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium">
                                        ✓ Free (Included in your plan)
                                    </div>
                                )}
                            </button>

                            {/* Security Scan */}
                            <button
                                onClick={() => setScanType('security')}
                                className={`relative p-5 rounded-xl border-2 transition-all text-left ${scanType === 'security'
                                    ? 'border-red-500 bg-red-50 dark:bg-red-950/30 shadow-lg shadow-red-500/20'
                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-red-300 dark:hover:border-red-700'
                                    }`}
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <span className="text-2xl">🛡️</span>
                                    {scanType === 'security' && (
                                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-500 text-white">
                                            Selected
                                        </span>
                                    )}
                                </div>
                                <h4 className="font-bold text-gray-900 dark:text-white mb-1">
                                    {t('scanType.security')}
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                    {t('cloudScan:benefits.security.title', 'Security Analysis')}
                                </p>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-2xl font-bold text-gray-900 dark:text-white">$4.99</span>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">/scan</span>
                                    <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                        50% off
                                    </span>
                                </div>
                                {usageInfo && usageInfo.canMakeFreeScan && (
                                    <div className="mt-2 px-2 py-1 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium">
                                        ✓ Free (Included in your plan)
                                    </div>
                                )}
                            </button>

                            {/* Cost Scan */}
                            <button
                                onClick={() => setScanType('cost')}
                                className={`relative p-5 rounded-xl border-2 transition-all text-left ${scanType === 'cost'
                                    ? 'border-green-500 bg-green-50 dark:bg-green-950/30 shadow-lg shadow-green-500/20'
                                    : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-green-300 dark:hover:border-green-700'
                                    }`}
                            >
                                <div className="flex items-start justify-between mb-2">
                                    <span className="text-2xl">💰</span>
                                    {scanType === 'cost' && (
                                        <span className="px-2 py-1 rounded-full text-xs font-semibold bg-green-500 text-white">
                                            Selected
                                        </span>
                                    )}
                                </div>
                                <h4 className="font-bold text-gray-900 dark:text-white mb-1">
                                    {t('scanType.cost')}
                                </h4>
                                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                    {t('cloudScan:benefits.cost.title', 'Cost Optimization')}
                                </p>
                                <div className="flex items-baseline gap-1">
                                    <span className="text-2xl font-bold text-gray-900 dark:text-white">$4.99</span>
                                    <span className="text-sm text-gray-500 dark:text-gray-400">/scan</span>
                                    <span className="ml-2 px-1.5 py-0.5 rounded text-xs font-semibold bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400">
                                        50% off
                                    </span>
                                </div>
                                {usageInfo && usageInfo.canMakeFreeScan && (
                                    <div className="mt-2 px-2 py-1 rounded bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400 text-xs font-medium">
                                        ✓ Free (Included in your plan)
                                    </div>
                                )}
                            </button>
                        </div>
                    </div>

                    {/* Validation Message */}
                    {validationMessage && (
                        <div className={`mt-6 p-4 rounded-lg ${validationMessage.type === 'success'
                            ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-800 dark:text-green-400'
                            : 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-800 dark:text-red-400'
                            }`}>
                            <div className="flex items-center gap-2">
                                {validationMessage.type === 'success' ? (
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                    </svg>
                                ) : (
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                    </svg>
                                )}
                                <span>{validationMessage.message}</span>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="mt-6 flex flex-wrap gap-3">
                        <button
                            onClick={handleValidateCredentials}
                            disabled={!canUseCloudScan || isValidating || isScanning}
                            className="px-5 py-2.5 rounded-lg font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                        >
                            {isValidating ? (
                                <span className="flex items-center gap-2">
                                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    {t('actions.validating')}
                                </span>
                            ) : (
                                `✓ ${t('actions.validateCredentials')}`
                            )}
                        </button>
                        <button
                            onClick={handleStartScan}
                            disabled={!canUseCloudScan || isValidating || isScanning}
                            className="px-5 py-2.5 rounded-lg font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30 transition-all"
                        >
                            {isScanning ? (
                                <span className="flex items-center gap-2">
                                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                    </svg>
                                    {t('actions.scanning')}
                                </span>
                            ) : (
                                `🚀 ${t('actions.startScan')}`
                            )}
                        </button>
                    </div>
                </div>
            )}

            {/* Scan Results */}
            {scanResult && (
                <div className="space-y-6">
                    {/* Results Header */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <div className={`inline-flex p-3 rounded-xl bg-gradient-to-br ${getProviderColor(scanResult.provider)} text-white shadow-lg`}>
                                <ProviderIcon provider={scanResult.provider} className="w-6 h-6" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h2 className="text-xl font-bold text-gray-900 dark:text-white">{t('results.title')}</h2>
                                    {hasPremiumAccess && (
                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-gradient-to-r from-indigo-500 to-purple-600 text-white">
                                            💎 Premium Access
                                        </span>
                                    )}
                                    {scanResult.isPaid && !hasPremiumAccess && (
                                        <span className="px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                                            ✓ Paid
                                        </span>
                                    )}
                                </div>
                                <p className="text-sm text-gray-600 dark:text-gray-400">
                                    {t('results.completedIn')} {formatDuration(scanResult.duration)} • {formatTimestamp(scanResult.timestamp)}
                                </p>
                            </div>
                        </div>
                        <button
                            onClick={() => {
                                setScanResult(null);
                                setSelectedProvider(null);
                            }}
                            className="px-4 py-2 rounded-lg font-medium bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
                        >
                            ← {t('actions.newScan')}
                        </button>
                    </div>

                    {/* Summary Cards */}
                    {scanResult.report && (
                        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="p-2 rounded-lg bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400">
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                        </svg>
                                    </div>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('results.summary.securityIssues')}</span>
                                </div>
                                <p className="text-3xl font-bold text-gray-900 dark:text-white">{scanResult.report.summary.totalSecurityFindings}</p>
                            </div>
                            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="p-2 rounded-lg bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400">
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                    </div>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('results.summary.costOptimizations')}</span>
                                </div>
                                <p className="text-3xl font-bold text-gray-900 dark:text-white">{scanResult.report.summary.totalCostRecommendations}</p>
                            </div>
                            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400">
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                                        </svg>
                                    </div>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('results.summary.potentialSavings')}</span>
                                </div>
                                <p className="text-3xl font-bold text-green-600 dark:text-green-400">
                                    {formatCurrency(scanResult.report.summary.estimatedTotalMonthlySavings || scanResult.report.summary.estimatedMonthlySavings || 0)}
                                    <span className="text-sm font-normal text-gray-500 dark:text-gray-400">{t('results.summary.perMonth')}</span>
                                </p>
                            </div>
                            <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm">
                                <div className="flex items-center gap-3 mb-2">
                                    <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400">
                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                        </svg>
                                    </div>
                                    <span className="text-sm text-gray-600 dark:text-gray-400">{t('results.summary.criticalIssues')}</span>
                                </div>
                                <p className="text-3xl font-bold text-red-600 dark:text-red-400">
                                    {scanResult.report?.summary?.severityBreakdown?.critical || scanResult.report?.severityBreakdown?.critical || 0}
                                </p>
                            </div>
                        </div>
                    )}

                    {/* Severity Breakdown */}
                    {scanResult.report && (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-6 shadow-sm">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">{t('results.severityBreakdown')}</h3>
                            <div className="flex flex-wrap gap-4">
                                {(['critical', 'high', 'medium', 'low', 'info'] as Severity[]).map((severity) => (
                                    <div key={severity} className={`flex items-center gap-2 px-4 py-2 rounded-lg border ${getSeverityColor(severity)}`}>
                                        <span className="font-bold">{scanResult.report?.summary?.severityBreakdown?.[severity] || scanResult.report?.severityBreakdown?.[severity] || 0}</span>
                                        <span>{t(`severity.${severity}`)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Tabs */}
                    <div className="border-b border-gray-200 dark:border-gray-800">
                        <div className="flex gap-4">
                            <button
                                onClick={() => setActiveTab('security')}
                                className={`px-4 py-3 font-medium border-b-2 transition-colors ${activeTab === 'security'
                                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                    }`}
                            >
                                🛡️ {t('results.tabs.security')} ({scanResult.security?.findings.length || 0})
                            </button>
                            <button
                                onClick={() => setActiveTab('cost')}
                                className={`px-4 py-3 font-medium border-b-2 transition-colors ${activeTab === 'cost'
                                    ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400'
                                    : 'border-transparent text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white'
                                    }`}
                            >
                                💰 {t('results.tabs.cost')} ({scanResult.cost?.recommendations.length || 0})
                            </button>
                        </div>
                    </div>

                    {/* Security Findings */}
                    {activeTab === 'security' && (
                        <div className="space-y-4">
                            {scanResult.security?.findings.length === 0 ? (
                                ((scanResult.isPaid === false || !scanResult.isPaid) && !hasPremiumAccess) && scanResult.report?.summary.totalSecurityFindings > 0 ? (
                                    <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 p-12 text-center">
                                        <div className="inline-flex p-4 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 mb-4">
                                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                                            {t('results.premiumRequired.title', 'Detalhes Disponíveis no Plano Premium')}
                                        </h3>
                                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                                            {t('results.premiumRequired.description', `Este scan encontrou ${scanResult.report?.summary.totalSecurityFindings || 0} problemas de segurança. Faça upgrade para ver os detalhes completos de cada problema.`)}
                                        </p>
                                        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                                            <button
                                                onClick={() => handlePurchaseScan(scanResult.scanId || '')}
                                                disabled={purchasingScanId === scanResult.scanId}
                                                className="px-6 py-3 rounded-lg font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30 transition-all flex items-center gap-2"
                                            >
                                                {purchasingScanId === scanResult.scanId ? (
                                                    <>
                                                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                                        </svg>
                                                        Processing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        Unlock Full Report - $9.99
                                                    </>
                                                )}
                                            </button>
                                            <Link
                                                to="/settings/billing"
                                                className="px-6 py-3 rounded-lg font-medium bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 border-2 border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                                            >
                                                Upgrade to Pro/Enterprise
                                            </Link>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-12 text-center">
                                        <div className="inline-flex p-4 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 mb-4">
                                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{t('results.noSecurityIssues.title')}</h3>
                                        <p className="text-gray-600 dark:text-gray-400">{t('results.noSecurityIssues.description')}</p>
                                    </div>
                                )
                            ) : (
                                scanResult.security?.findings.map((finding) => (
                                    <div
                                        key={finding.id}
                                        className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                                    >
                                        <button
                                            onClick={() => setExpandedFinding(expandedFinding === finding.id ? null : finding.id)}
                                            className="w-full p-4 text-left"
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        <span className={`px-2.5 py-1 rounded-md text-xs font-semibold border ${getSeverityColor(finding.severity)}`}>
                                                            {t(`severity.${finding.severity}`).toUpperCase()}
                                                        </span>
                                                        <span className="text-sm text-gray-500 dark:text-gray-400">{finding.category}</span>
                                                    </div>
                                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{finding.title}</h4>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">{finding.resource}</p>
                                                </div>
                                                <svg
                                                    className={`w-5 h-5 text-gray-400 transition-transform ${expandedFinding === finding.id ? 'rotate-180' : ''}`}
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </button>
                                        {expandedFinding === finding.id && (
                                            <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-800 pt-4">
                                                <div className="space-y-3">
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-900 dark:text-white">{t('results.finding.description')}</span>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{finding.description}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-900 dark:text-white">{t('results.finding.recommendation')}</span>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{finding.recommendation}</p>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2 pt-2">
                                                        <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-xs text-gray-600 dark:text-gray-400">
                                                            {t('results.finding.type')}: {finding.resourceType}
                                                        </span>
                                                        {finding.region && (
                                                            <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-xs text-gray-600 dark:text-gray-400">
                                                                {t('results.finding.region')}: {finding.region}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {/* Cost Recommendations */}
                    {activeTab === 'cost' && (
                        <div className="space-y-4">
                            {scanResult.cost?.recommendations.length === 0 ? (
                                ((scanResult.isPaid === false || !scanResult.isPaid) && !hasPremiumAccess) && scanResult.report?.summary.totalCostRecommendations > 0 ? (
                                    <div className="rounded-xl border border-indigo-200 dark:border-indigo-800 bg-indigo-50 dark:bg-indigo-900/20 p-12 text-center">
                                        <div className="inline-flex p-4 rounded-full bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 mb-4">
                                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                                            {t('results.premiumRequired.title', 'Detalhes Disponíveis no Plano Premium')}
                                        </h3>
                                        <p className="text-gray-600 dark:text-gray-400 mb-6">
                                            {t('results.premiumRequired.description', `Este scan encontrou ${scanResult.report?.summary.totalCostRecommendations || 0} oportunidades de otimização de custo. Faça upgrade para ver os detalhes completos de cada recomendação.`)}
                                        </p>
                                        <div className="flex flex-col sm:flex-row gap-3 justify-center items-center">
                                            <button
                                                onClick={() => handlePurchaseScan(scanResult.scanId || '')}
                                                disabled={purchasingScanId === scanResult.scanId}
                                                className="px-6 py-3 rounded-lg font-medium bg-gradient-to-r from-indigo-500 to-purple-600 text-white hover:from-indigo-600 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-indigo-500/30 transition-all flex items-center gap-2"
                                            >
                                                {purchasingScanId === scanResult.scanId ? (
                                                    <>
                                                        <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                                                        </svg>
                                                        Processing...
                                                    </>
                                                ) : (
                                                    <>
                                                        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                        </svg>
                                                        Unlock Full Report - $9.99
                                                    </>
                                                )}
                                            </button>
                                            <Link
                                                to="/settings/billing"
                                                className="px-6 py-3 rounded-lg font-medium bg-white dark:bg-gray-800 text-indigo-600 dark:text-indigo-400 border-2 border-indigo-500 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                                            >
                                                Upgrade to Pro/Enterprise
                                            </Link>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-12 text-center">
                                        <div className="inline-flex p-4 rounded-full bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 mb-4">
                                            <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">{t('results.noCostOptimizations.title')}</h3>
                                        <p className="text-gray-600 dark:text-gray-400">{t('results.noCostOptimizations.description')}</p>
                                    </div>
                                )
                            ) : (
                                scanResult.cost?.recommendations.map((rec) => (
                                    <div
                                        key={rec.id}
                                        className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                                    >
                                        <button
                                            onClick={() => setExpandedFinding(expandedFinding === rec.id ? null : rec.id)}
                                            className="w-full p-4 text-left"
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-3 mb-2">
                                                        {rec.estimatedMonthlySavings && rec.estimatedMonthlySavings > 0 && (
                                                            <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800">
                                                                {t('results.finding.save')} {formatCurrency(rec.estimatedMonthlySavings)}{t('results.summary.perMonth')}
                                                            </span>
                                                        )}
                                                        <span className="text-sm text-gray-500 dark:text-gray-400">{rec.category}</span>
                                                    </div>
                                                    <h4 className="font-semibold text-gray-900 dark:text-white mb-1">{rec.title}</h4>
                                                    <p className="text-sm text-gray-600 dark:text-gray-400">{rec.resource}</p>
                                                </div>
                                                <svg
                                                    className={`w-5 h-5 text-gray-400 transition-transform ${expandedFinding === rec.id ? 'rotate-180' : ''}`}
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </div>
                                        </button>
                                        {expandedFinding === rec.id && (
                                            <div className="px-4 pb-4 border-t border-gray-100 dark:border-gray-800 pt-4">
                                                <div className="space-y-3">
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-900 dark:text-white">{t('results.finding.description')}</span>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{rec.description}</p>
                                                    </div>
                                                    <div>
                                                        <span className="text-sm font-medium text-gray-900 dark:text-white">{t('results.finding.recommendation')}</span>
                                                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{rec.recommendation}</p>
                                                    </div>
                                                    <div className="flex flex-wrap gap-2 pt-2">
                                                        <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-xs text-gray-600 dark:text-gray-400">
                                                            {t('results.finding.type')}: {rec.resourceType}
                                                        </span>
                                                        {rec.region && (
                                                            <span className="px-2 py-1 rounded-md bg-gray-100 dark:bg-gray-800 text-xs text-gray-600 dark:text-gray-400">
                                                                {t('results.finding.region')}: {rec.region}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                    )}

                    {/* Errors */}
                    {scanResult.errors && scanResult.errors.length > 0 && (
                        <div className="rounded-xl border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 p-4">
                            <h3 className="font-semibold text-yellow-800 dark:text-yellow-400 mb-2">⚠️ {t('results.warnings')}</h3>
                            <ul className="list-disc list-inside space-y-1">
                                {scanResult.errors.map((error, index) => (
                                    <li key={index} className="text-sm text-yellow-700 dark:text-yellow-400">{error}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            )}
        </MainLayout>
    );
}
